import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Zap } from 'lucide-react';

const SHOWN_KEY = 'odyvoy_boost_exit_intent_shown';

interface Props {
  onGrab: () => void;
}

export const BoostExitIntentPopup = ({ onGrab }: Props) => {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loading || user) return;
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(SHOWN_KEY) === '1') return;

    const handleMouseOut = (e: MouseEvent) => {
      // Trigger when mouse leaves top of viewport
      if (e.clientY <= 0 && !e.relatedTarget) {
        sessionStorage.setItem(SHOWN_KEY, '1');
        setOpen(true);
        document.removeEventListener('mouseout', handleMouseOut);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mouseout', handleMouseOut);
    }, 3000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [user, loading]);

  const handleGrab = () => {
    setOpen(false);
    onGrab();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <DialogTitle className="text-center text-2xl">Wait — one trip on us?</DialogTitle>
          <DialogDescription className="text-center text-base pt-1">
            Grab a <span className="font-bold text-foreground">3-Day Premium Economy Boost for just €2.99</span>.
            Full AI packing, visa checks, weather &amp; health prep — no subscription, no auto-renewal.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 pt-2">
          <Button
            onClick={handleGrab}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:brightness-110 text-white font-semibold h-12"
          >
            Grab my €2.99 Boost
          </Button>
          <Button variant="ghost" onClick={() => setOpen(false)} className="w-full">
            No thanks, maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
